import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiCog, HiRefresh } from 'react-icons/hi';
import apiClient from '../../api/client';
import { useNotification } from '../../context/NotificationContext';
import MpDailyChart, { KpiGrid, mpFmt as fmt } from '../../components/MpDailyChart';
import MarketplaceStrategyModal from '../../components/MarketplaceStrategyModal';
import { formatMarketplaceStrategy, strategyFormToPayload } from '../../utils/marketplaceStrategies';

export default function AvitoListingsPromo({ clientId }) {
  const { notify } = useNotification();
  const [listings, setListings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editListing, setEditListing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        apiClient.get('/avito/listings/manage', { params: { client_id: clientId } }),
        apiClient.get('/avito/listings/summary', { params: { client_id: clientId } }),
      ]);
      setListings(listRes.data?.items || []);
      setSummary(summaryRes.data);
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, notify]);

  useEffect(() => { load(); }, [load]);

  const promoStats = useMemo(() => {
    const withBid = listings.filter((l) => Number(l.bid_kopecks) > 0);
    const avgBid = withBid.length
      ? withBid.reduce((s, l) => s + Number(l.bid_kopecks), 0) / withBid.length / 100
      : 0;
    const bidderOn = listings.filter((l) => l.bidder_enabled).length;
    return { withBid: withBid.length, avgBid, bidderOn };
  }, [listings]);

  const saveListing = async (values) => {
    try {
      const payload = strategyFormToPayload(values, 'avito_listing');
      await apiClient.patch(`/avito/listings/${editListing.item_id}`, {
        client_id: clientId,
        ...payload,
        bidder_enabled: values.bidder_enabled ? 1 : 0,
        automation_enabled: values.automation_enabled ? 1 : 0,
      });
      notify('Сохранено', 'success');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка сохранения', 'error');
      throw e;
    }
  };

  if (loading) return <p className="text-[var(--wb-text-muted)]">Загрузка…</p>;

  const totals = summary?.totals || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Продвижение объявлений</h2>
          <p className="text-sm text-[var(--wb-text-muted)]">Ставки, позиции и биддер — инструмент Авито для объявлений</p>
        </div>
        <button type="button" className="wb-btn-secondary" onClick={load}>
          <HiRefresh className="w-4 h-4" />
        </button>
      </div>

      <KpiGrid items={[
        { label: 'С активной ставкой', value: fmt(promoStats.withBid) },
        { label: 'Средняя ставка', value: promoStats.avgBid ? `${fmt(promoStats.avgBid, 0)} ₽` : '—' },
        { label: 'Биддер включён', value: fmt(promoStats.bidderOn) },
        { label: 'Просмотры', value: fmt(totals.views) },
        { label: 'Контакты', value: fmt(totals.contacts) },
        { label: 'В избранное', value: fmt(totals.favorites) },
        { label: 'Конверсия', value: `${fmt(totals.conversion_pct, 1)}%` },
      ]} />

      <MpDailyChart
        data={summary?.daily || []}
        lines={[
          { key: 'views', name: 'Просмотры', color: '#4f46e5' },
          { key: 'contacts', name: 'Контакты', color: '#10b981' },
          { key: 'favorites', name: 'В избранное', color: '#f97316' },
          { key: 'conversion_pct', name: 'Конверсия, %', color: '#14b8a6', yAxisId: 'right' },
        ]}
      />

      <div className="wb-card overflow-x-auto">
        <table className="wb-data-table">
          <thead>
            <tr>
              <th>Объявление</th>
              <th className="wb-col-num">Просмотры</th>
              <th className="wb-col-num">Контакты</th>
              <th className="wb-col-num">Ставка</th>
              <th className="wb-col-num">Позиция</th>
              <th>Стратегия</th>
              <th className="wb-col-actions" aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.item_id}>
                <td className="font-medium">{l.title}</td>
                <td className="wb-col-num">{fmt(l.views)}</td>
                <td className="wb-col-num">{fmt(l.contacts)}</td>
                <td className="wb-col-num">{l.bid_kopecks ? `${(l.bid_kopecks / 100).toFixed(0)} ₽` : '—'}</td>
                <td className="wb-col-num">{l.position ?? '—'}</td>
                <td>
                  <div className="text-xs text-gray-600 max-w-[140px]">{formatMarketplaceStrategy(l, 'avito_listing')}</div>
                </td>
                <td className="wb-col-actions">
                  <button type="button" onClick={() => setEditListing({ ...l })} aria-label="Настройки">
                    <HiCog className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MarketplaceStrategyModal
        open={Boolean(editListing)}
        onClose={() => setEditListing(null)}
        title={editListing?.title}
        subtitle={editListing?.city ? `${editListing.city} · ID ${editListing.item_id}` : `ID ${editListing?.item_id}`}
        marketplace="avito_listing"
        initialRow={editListing}
        onSave={saveListing}
        showBidLimits={false}
        showBudget={false}
      />
    </div>
  );
}
